import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Modal } from '@components/common/Modal/Modal';
import { PageTitle } from '@components/common/PageTitle/PageTitle';
import { UTable } from '@components/common/UTable/UTable';
import { Typography } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { useGetDictionariesQuery } from '@/api/dictionaries/api';
import { EventProtocolTypeDictionaryItem } from '@/api/dictionaries/types';
import {
  useCreateDosageMutation,
  useCreateProtocolMutation,
  useDeleteDosageMutation,
  useDeleteProtocolMutation,
  useGetProtocolsQuery,
  useUpdateDosageMutation,
  useUpdateProtocolMutation,
} from '@/api/vaccination/protocols/api';
import {
  addDosageSchema,
  addSchema,
} from '@/pages/Vaccination/components/protocols/add.config';
import { columns } from '@/pages/Vaccination/components/protocols/columns.config';
import {
  EventProtocol,
  EventProtocolDosageItem,
  EventProtocolDosagePayload,
  EventProtocolItem,
  EventProtocolPayload,
} from '@/pages/Vaccination/components/protocols/types';
import { OrganizationLevelType } from '@/shared/organizations/enums';
import { vaccination } from '@/shared/security/permissions/vaccination';
import { mapTableIndexWithPage } from '@/utils/utils';

import cl from './style.module.scss';

type ProtocolTypeSection = {
  key: string;
  title: string;
  types: EventProtocolTypeDictionaryItem[];
};

export const VaccinationProtocols = () => {
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get('organizationId') || '';
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [currentData, setCurrentData] = useState<Record<string, any>>({});

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isAddDosageModalOpen, setIsAddDosageModalOpen] = useState<boolean>(false);

  const { data: dictionaries, isFetching } = useGetDictionariesQuery();
  const { data: records } = useGetProtocolsQuery(
    { organizationId },
    { skip: !organizationId },
  );

  const [createProtocol] = useCreateProtocolMutation();
  const [updateProtocol] = useUpdateProtocolMutation();
  const [deleteProtocol] = useDeleteProtocolMutation();

  const [createDosage] = useCreateDosageMutation();
  const [updateDosage] = useUpdateDosageMutation();
  const [deleteDosage] = useDeleteDosageMutation();

  // Типы схем, сгруппированные по группам (видам) животных; типы без группы — в конце
  const sections = useMemo<ProtocolTypeSection[]>(() => {
    const types = (dictionaries?.EVENT_PROTOCOL_TYPE ||
      []) as EventProtocolTypeDictionaryItem[];
    const speciesOf = (type: EventProtocolTypeDictionaryItem) =>
      type.animalSpeciesId ?? records?.[type.id]?.animalSpeciesId ?? null;
    const orderOf = (type: EventProtocolTypeDictionaryItem) =>
      type.orderNumber ?? records?.[type.id]?.orderNumber ?? 0;
    const sortedTypes = [...types].sort((a, b) => orderOf(a) - orderOf(b));

    const speciesSections = (dictionaries?.ANIMAL_SPECIES || []).map((species) => ({
      key: species.id,
      title: species.name,
      types: sortedTypes.filter((type) => speciesOf(type) === species.id),
    }));

    const unassignedTypes = sortedTypes.filter((type) => {
      const speciesId = speciesOf(type);
      return !speciesId || !dictionaries?.dictMap?.ANIMAL_SPECIES?.[speciesId];
    });

    return unassignedTypes.length > 0
      ? [
          ...speciesSections,
          { key: 'unassigned', title: 'Без привязки к группе', types: unassignedTypes },
        ]
      : speciesSections;
  }, [dictionaries, records]);

  const modalData = useMemo(() => {
    return currentData?.action === 'EDIT' ? currentData : {};
  }, [currentData]);

  const dosageModalData = useMemo(() => {
    return currentData?.action === 'EDIT_DOSAGE' ? currentData : {};
  }, [currentData]);

  const submitModal = useCallback(
    async (record: EventProtocolItem | EventProtocolDosageItem) => {
      const { action } = currentData;

      switch (action) {
        case 'ADD':
          await createProtocol({
            ...(record as EventProtocolItem),
            organizationId,
            eventProtocolTypeId: currentData.eventProtocolTypeId,
          }).unwrap();
          setIsModalOpen(false);
          break;

        case 'EDIT':
          await updateProtocol({
            ...(record as EventProtocolItem),
            organizationId,
            eventProtocolTypeId: currentData.eventProtocolTypeId,
            id: currentData.id,
          }).unwrap();
          setIsModalOpen(false);
          break;

        case 'ADD_DOSAGE':
          await createDosage({
            ...(record as EventProtocolDosageItem),
            eventProtocolId: currentData?.id,
          }).unwrap();
          setIsAddDosageModalOpen(false);
          break;

        case 'EDIT_DOSAGE':
          await updateDosage({
            ...(record as EventProtocolDosageItem),
            id: currentData.id,
            eventProtocolId: currentData?.eventProtocolId,
          }).unwrap();
          setIsAddDosageModalOpen(false);
          break;

        default:
          break;
      }

      setCurrentData({});
    },
    [
      currentData,
      createProtocol,
      updateProtocol,
      createDosage,
      updateDosage,
      organizationId,
    ],
  );

  const isDosageRow = (record: Record<string, any>) =>
    Boolean(record?.eventProtocolId) && !record?.dosages;

  return (
    <>
      <div className={cl.wrap}>
        {sections.map((section) => (
          <section key={section.key} className={cl.section}>
            <PageTitle title={section.title} className={cl.sectionTitle} />
            {section.types.length === 0 && (
              <Typography.Text type={'secondary'} className={cl.empty}>
                Для группы не назначены типы схем мероприятий
              </Typography.Text>
            )}
            {section.types.map((item) => (
              <div key={item.id}>
                <PageTitle level={4} title={item.name} />
                <UTable
                  size={'small'}
                  data={mapTableIndexWithPage(
                    records?.[item.id]?.eventProtocols || [],
                  ).map((row: Record<string, any>) => {
                    if (Array.isArray(row.dosages) && row.dosages.length === 0) {
                      const { dosages, ...rest } = row; // удаляем пустой children-ключ
                      void dosages;
                      return rest;
                    }
                    return row;
                  })}
                  columns={columns(dictionaries?.dictMap)}
                  loading={isFetching}
                  expandable={{
                    childrenColumnName: 'dosages',
                    rowExpandable: (record) =>
                      Array.isArray(record.dosages) && record.dosages.length > 0,
                  }}
                  buttons={[
                    {
                      addBtn: true,
                      action: () => {
                        setCurrentData({ eventProtocolTypeId: item.id, action: 'ADD' });
                        setIsModalOpen(true);
                      },
                      security: {
                        permissions: vaccination.eventProtocol.add,
                        level: [
                          OrganizationLevelType.DISTRICT,
                          OrganizationLevelType.ORGANIZATION,
                        ],
                      },
                    },
                  ]}
                  actions={(record: Record<string, any>) => {
                    if (isDosageRow(record)) {
                      return [
                        {
                          icon: <EditOutlined />,
                          action: (row: EventProtocolDosagePayload) => {
                            setCurrentData({ ...row, action: 'EDIT_DOSAGE' });
                            setIsAddDosageModalOpen(true);
                          },
                          security: {
                            permissions: vaccination.eventProtocol.edit,
                            level: [
                              OrganizationLevelType.DISTRICT,
                              OrganizationLevelType.ORGANIZATION,
                            ],
                          },
                        },
                        {
                          icon: <DeleteOutlined />,
                          action: (row: EventProtocolDosagePayload) => {
                            setCurrentData({ id: row.id, action: 'DELETE_DOSAGE' });
                            setIsDeleteModalOpen(true);
                          },
                          security: {
                            permissions: vaccination.eventProtocol.delete,
                            level: [
                              OrganizationLevelType.DISTRICT,
                              OrganizationLevelType.ORGANIZATION,
                            ],
                          },
                        },
                      ];
                    }

                    return [
                      {
                        icon: <EditOutlined />,
                        action: (row: EventProtocolPayload) => {
                          setCurrentData({ ...row, action: 'EDIT' });
                          setIsModalOpen(true);
                        },
                        security: {
                          permissions: vaccination.eventProtocol.edit,
                          level: [
                            OrganizationLevelType.DISTRICT,
                            OrganizationLevelType.ORGANIZATION,
                          ],
                        },
                      },
                      {
                        icon: <DeleteOutlined />,
                        action: (row: EventProtocol) => {
                          setCurrentData({ id: row.id, action: 'DELETE_PROTOCOL' });
                          setIsDeleteModalOpen(true);
                        },
                        security: {
                          permissions: vaccination.eventProtocol.delete,
                          level: [
                            OrganizationLevelType.DISTRICT,
                            OrganizationLevelType.ORGANIZATION,
                          ],
                        },
                      },
                      {
                        icon: <PlusOutlined />,
                        action: (row: EventProtocol) => {
                          setCurrentData({ ...row, action: 'ADD_DOSAGE' });
                          setIsAddDosageModalOpen(true);
                        },
                        security: {
                          permissions: vaccination.eventProtocol.edit,
                          level: [
                            OrganizationLevelType.DISTRICT,
                            OrganizationLevelType.ORGANIZATION,
                          ],
                        },
                      },
                    ];
                  }}
                />
              </div>
            ))}
          </section>
        ))}
      </div>

      <Modal
        open={isModalOpen}
        mode={'form'}
        title={
          currentData.action === 'ADD'
            ? 'Добавить мероприятие'
            : 'Редактировать мероприятие'
        }
        data={modalData}
        schema={addSchema(dictionaries)}
        onSubmit={(record) => submitModal(record)}
        onCancel={() => {
          setIsModalOpen(false);
          setCurrentData({});
        }}
        width={'375px'}
        layout={{
          formLayout: 'vertical',
          itemLayout: 'vertical',
        }}
        destroyOnHidden={true}
      />

      <Modal
        open={isAddDosageModalOpen}
        mode={'form'}
        title={
          currentData.action === 'ADD_DOSAGE'
            ? 'Добавить дозировку'
            : 'Редактировать дозировку'
        }
        data={dosageModalData}
        schema={addDosageSchema}
        onSubmit={(record) => submitModal(record)}
        onCancel={() => {
          setIsAddDosageModalOpen(false);
          setCurrentData({});
        }}
        width={'375px'}
        layout={{
          formLayout: 'vertical',
          itemLayout: 'vertical',
        }}
        destroyOnHidden={true}
      />

      <Modal
        open={isDeleteModalOpen}
        mode={'confirm'}
        text={
          'Вы уверены, что хотите удалить выбранные данные? При выборе варианта «Да» данные восстановлению не подлежат'
        }
        onSubmit={async () => {
          switch (currentData.action) {
            case 'DELETE_DOSAGE':
              await deleteDosage({ id: currentData.id }).unwrap();
              break;

            case 'DELETE_PROTOCOL':
            default:
              await deleteProtocol({ id: currentData.id }).unwrap();
              break;
          }

          setIsDeleteModalOpen(false);
          setCurrentData({});
        }}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setCurrentData({});
        }}
      />
    </>
  );
};
